/**
 * Veri yaşam döngüsü metinleri (M6 WP 6.4). Metinler TEKNİK yetenekleri anlatır; hiçbir metin hukuki uygunluk iddiası taşımaz
 * (değerlendirme çalışma alanı sahibinindir).
 */
import {
  CLOSURE_FREEZE_DAYS,
  SUPPORT_ACCESS_MAX_HOURS,
  TENANT_RETENTION,
  type EraseMode,
  type SubjectRequestKind,
  type SubjectRequestStatus,
} from '@castinflow/shared';

const NUM = new Intl.NumberFormat('tr-TR');
export const formatCount = (n: number): string => NUM.format(n);

export const lifecycleText = {
  legalNote:
    'Bu ekrandaki işlemler teknik yeteneklerdir. Hangi verinin ne zaman silineceğine ya da saklanacağına ilişkin hukuki değerlendirme çalışma alanı sahibinin sorumluluğundadır.',
  loadFailed: 'Bilgi yüklenemedi. Sayfayı yenileyip yeniden deneyin.',
  /** Yönetici olmayan kullanıcı: yetki sınırı açıkça söylenir ("yüklenemedi" hatası gibi görünmez; uç çağrılmaz) */
  adminOnly: 'Bu ekranı yalnız çalışma alanı yöneticileri kullanabilir.',
  saved: 'Kaydedildi',
  saveFailed: 'Kaydedilemedi',
  forbidden: 'Bu işlem için yetkiniz yok.',
  rateLimited: 'Çok fazla deneme yapıldı. Bir süre sonra yeniden deneyin.',
  reauthFailed: 'Kimlik doğrulaması başarısız: parolanızı ve doğrulama kodunuzu denetleyin.',
  confirmMismatch: 'Onay metni eşleşmiyor.',
  password: 'Parolanız',
  passwordHint: 'Geri alınamaz işlemlerden önce parolanız yeniden istenir.',
  totp: 'Doğrulama kodu',
  totpHint: 'İki adımlı doğrulama açıksa 6 haneli kodu girin.',
  cancel: 'Vazgeç',
  close: 'Kapat',

  // --- Sayfa: veri yaşam döngüsü ---
  page: {
    title: 'Veri yaşam döngüsü',
    description:
      'Saklama süreleri, kişi ve aday verisi için dışa aktarma ve silme talepleri, silme defteri, çalışma alanını dışa aktarma ve hesap kapatma.',
  },
  retention: {
    title: 'Saklama süreleri',
    intro:
      'Süresi dolan veri arka planda, küçük partilerle kalıcı olarak temizlenir. Temizlenen veri geri getirilemez.',
    deletedRecords: 'Silinen kayıtların saklanma süresi (gün)',
    deletedRecordsHint: `Silinen kayıt bu süre boyunca geri alınabilir; sonra ekleriyle birlikte kalıcı olarak temizlenir. ${String(TENANT_RETENTION.deletedRecordDays.min)}–${String(TENANT_RETENTION.deletedRecordDays.max)} gün.`,
    audit: 'Denetim kayıtlarının saklanma süresi (ay)',
    auditHint: `Bu süreden eski denetim kayıtları silinir. ${String(TENANT_RETENTION.auditMonths.min)}–${String(TENANT_RETENTION.auditMonths.max)} ay.`,
    isDefault: 'Varsayılan değer kullanılıyor',
    save: 'Süreleri kaydet',
    reset: 'Varsayılana dön',
    rangeError: (min: number, max: number) =>
      `${String(min)} ile ${String(max)} arasında bir tam sayı girin.`,
    platformTitle: 'Platform tarafından belirlenen süreler',
    platformIntro: 'Bu süreler çalışma alanından değiştirilemez.',
    platform: {
      jobResultsDays: 'Tamamlanan arka plan işi kayıtları',
      notificationsDays: 'Uygulama içi bildirimler',
      domainEventsDays: 'İşlenmiş olay kuyruğu satırları',
      sessionsDays: 'Kapanmış oturumlar ve kullanılmış bağlantılar',
    },
    days: (n: number) => `${formatCount(n)} gün`,
  },
  requests: {
    title: 'Kişi ve aday verisi talepleri',
    intro:
      'Bir kişinin ya da adayın bütün verisini dışa aktarmak ya da silmek için kaydın sayfasındaki "Komutlar" menüsünden "Kişisel veri işlemleri"ni açın.',
    empty: 'Henüz talep yok.',
    columns: { record: 'Kayıt', kind: 'Talep', status: 'Durum', created: 'Tarih', by: 'İsteyen' },
    open: 'Aç',
    recordLabel: (type: string, no: number | null, id: string) =>
      `${type} ${no === null ? id.slice(0, 8) : `#${String(no)}`}`,
  },
  kinds: { export: 'Dışa aktarma', erase: 'Silme' } satisfies Record<SubjectRequestKind, string>,
  statuses: {
    queued: 'Sırada',
    running: 'Hazırlanıyor',
    ready: 'Hazır',
    confirmed: 'Onaylandı, yürütülüyor',
    completed: 'Tamamlandı',
    failed: 'Tamamlanamadı',
    expired: 'Süresi doldu',
    cancelled: 'Vazgeçildi',
  } satisfies Record<SubjectRequestStatus, string>,
  ledger: {
    title: 'Silme defteri',
    intro:
      'Kalıcı silme ve anonimleştirme işlemlerinin kişisel veri içermeyen izi. Yedekten geri yükleme yapılırsa bu defter yeniden uygulanır; silinen veri geri gelmez.',
    empty: 'Henüz kalıcı silme işlemi yok.',
    columns: { when: 'Zaman', scope: 'İşlem', record: 'Kayıt', mode: 'Kip' },
  },
  closure: {
    title: 'Hesabı kapat',
    intro: (days: number) =>
      `Kapatma başlatıldığında çalışma alanı ${String(days)} gün dondurulur: kimse giriş yapamaz, e-posta ve web kancası gibi işler durur. Süre sonunda bütün veriler ve dosyalar kalıcı olarak silinir. Dondurma süresince sahip kapatmayı iptal edebilir.`,
    ownerOnly: 'Hesabı yalnız çalışma alanı sahibi kapatabilir.',
    start: 'Hesabı kapatmayı başlat',
    dialogTitle: 'Hesabı kapat',
    dialogWarning: (days: number) =>
      `Bu işlem bütün kullanıcıların oturumunu hemen sonlandırır. ${String(days)} gün sonra veriler kalıcı olarak silinir ve geri getirilemez.`,
    confirmLabel: (slug: string) => `Onaylamak için çalışma alanı kısa adını yazın: ${slug}`,
    submit: 'Kapatmayı başlat',
    started: 'Hesap kapatma başlatıldı',
    cancelHint:
      'İptal etmek için giriş sayfasındaki "Hesap kapatmayı iptal et" bağlantısını kullanın. Dondurma süresince giriş kapalıdır.',
    notActive: 'Çalışma alanı etkin değil.',
    defaultDays: CLOSURE_FREEZE_DAYS.default,
  },

  // --- Sayfa: kişisel veri işlemleri (kayıt) ---
  subject: {
    title: (label: string) => `Kişisel veri işlemleri — ${label}`,
    pageTitle: 'Kişisel veri işlemleri',
    description:
      'Bu kayda ait bütün veriyi dışa aktarabilir ya da kaydı kalıcı olarak silebilir / anonimleştirebilirsiniz. Her adım denetim kayıtlarına yazılır.',
    back: 'Veri yaşam döngüsüne dön',
    exportTitle: 'Veriyi dışa aktar',
    exportIntro:
      'Kayıt alanları, özel alanlar, iletişim bilgileri, notlar, aktiviteler, e-posta kayıtları, belge atıfları ve dosyalar tek bir arşivde toplanır. Paket çalışma alanındaki bütün ilişkili veriyi içerir.',
    exportStart: 'Veri paketi hazırla',
    exportPreparing: 'Paket hazırlanıyor…',
    exportReady: (size: string) => `Paket hazır (${size}).`,
    exportExpires: (when: string) => `${when} tarihinde kendiliğinden silinir.`,
    exportDownload: 'Paketi indir',
    exportFailed: 'Paket hazırlanamadı.',
    exportExpired: 'Paketin süresi doldu; yeniden hazırlayabilirsiniz.',
    downloadFailed: 'İndirme başlatılamadı.',
    eraseTitle: 'Sil ya da anonimleştir',
    eraseIntro:
      'Önce ön izleme hazırlanır: neyin silineceği, neyin anonimleştirileceği ve neyin korunacağı sayılarla gösterilir. Yürütme için ayrıca onay ve parola istenir.',
    modeLabel: 'Yöntem',
    modes: {
      delete: 'Kalıcı sil — kayıt ve ilişkili veriler tamamen silinir',
      anonymize:
        'Anonimleştir — kişisel alanlar temizlenir, kayıt satırı belgelerin bütünlüğü için anonim olarak kalır',
    } satisfies Record<EraseMode, string>,
    previewStart: 'Ön izleme hazırla',
    previewPreparing: 'Ön izleme hazırlanıyor…',
    previewTitle: 'Ön izleme',
    previewExpires: (when: string) => `Ön izleme ${when} tarihine kadar onaylanabilir.`,
    deleted: 'Kalıcı olarak silinecek',
    anonymized: 'Kişisel alanları temizlenecek (satır korunur)',
    retained: 'Korunacak',
    objects: (n: number) => `Dosya deposundan silinecek dosya: ${formatCount(n)}`,
    queueJobs: (n: number) => `Kuyruktan kaldırılacak bekleyen iş: ${formatCount(n)}`,
    none: 'Yok',
    count: (n: number) => formatCount(n),
    referencedTitle: 'Bu kayıt kalıcı olarak silinemez',
    referencedBody:
      'Kayıt başka kayıtlar tarafından gösteriliyor (ör. kesinleşmiş bir teklifin ilgili kişisi). Belge bütünlüğünü korumak için "Anonimleştir" yöntemini kullanın.',
    referencedList: 'Başvuran kayıtlar',
    notFound: 'Kayıt bulunamadı ya da daha önce silinmiş.',
    confirmTitle: 'Onay',
    confirmWarning:
      'Bu işlem geri alınamaz. Silinen veri yedekten geri yükleme yapılsa bile yeniden silinir.',
    confirmLabel: (text: string) => `Onaylamak için şunu yazın: ${text}`,
    confirmSubmit: { delete: 'Kalıcı olarak sil', anonymize: 'Anonimleştir' } satisfies Record<
      EraseMode,
      string
    >,
    discard: 'Talepten vazgeç',
    running: 'İşlem yürütülüyor…',
    doneTitle: 'İşlem tamamlandı',
    doneMatched: 'Sonuç ön izlemeyle aynı.',
    doneDiffers: 'Ön izlemeden sonra veri değiştiği için sonuç sayıları ön izlemeden farklı.',
    resultDeleted: 'Kalıcı olarak silindi',
    resultAnonymized: 'Kişisel alanları temizlendi',
    resultRetained: 'Korundu',
    failed: 'İşlem tamamlanamadı.',
    alreadyErased: 'Bu kayıt daha önce anonimleştirilmiş.',
    openRequest: 'Bu kayıt için sonuçlanmamış bir silme talebi var.',
    newRequest: 'Yeni talep',
  },

  // --- Sayfa: denetim kayıtları ---
  audit: {
    title: 'Denetim kayıtları',
    description: (months: number) =>
      `Çalışma alanında kim, ne zaman, ne yaptı. Kayıtlar ${String(months)} ay saklanır; ayrıntıdaki hassas alanlar maskelenir.`,
    filters: 'Süzgeçler',
    from: 'Başlangıç',
    to: 'Bitiş',
    area: 'Alan',
    areaAll: 'Tüm alanlar',
    action: 'Eylem kodu (ya da ön eki)',
    actionHint: 'Örnek: auth.login ya da ön ek olarak data_subject.',
    user: 'Kullanıcı kimliği',
    record: 'Kayıt kimliği',
    supportOnly: 'Yalnız destek erişimi',
    apply: 'Uygula',
    clear: 'Temizle',
    invalidFilter: 'Süzgeç değerlerini denetleyin (kimlikler uuid biçiminde, eylem küçük harfle).',
    empty: 'Bu süzgeçle eşleşen kayıt yok.',
    columns: {
      when: 'Zaman',
      action: 'Eylem',
      actor: 'Kullanıcı',
      entity: 'Varlık',
      detail: 'Ayrıntı',
    },
    system: 'Sistem',
    supportBadge: 'Destek erişimi',
    more: 'Daha eski kayıtlar',
    export: 'CSV olarak indir',
    exported: (n: number, truncated: boolean) =>
      truncated
        ? `${formatCount(n)} kayıt indirildi (üst sınır; süzgeci daraltın).`
        : `${formatCount(n)} kayıt indirildi.`,
    exportFailed: 'Dışa aktarma başarısız.',
    showDetail: 'Ayrıntıyı göster',
    tableLabel: 'Denetim kayıtları',
  },

  // --- Sayfa: destek erişimi ---
  support: {
    title: 'Destek erişimi',
    description:
      'Castinflow destek ekibinin çalışma alanınıza erişebilmesi için süreli ve salt-okunur izin verin. İzin vermediğiniz sürece destek ekibi verinize erişemez.',
    how: 'Nasıl çalışır',
    howItems: [
      `İzin en çok ${String(SUPPORT_ACCESS_MAX_HOURS)} saat sürer ve istediğiniz an iptal edilebilir.`,
      'İzin verince tek kullanımlık bir kod üretilir; kodu destek görevlisine siz iletirsiniz.',
      'Destek oturumu salt-okunurdur: kayıt oluşturamaz, değiştiremez, silemez; kullanıcı yönetimi, dışa aktarma ve dosya indirme kapalıdır.',
      'Oturum sizin görünümünüzle çalışır ve her isteği denetim kayıtlarına "destek erişimi" olarak yazılır.',
    ],
    grant: 'Destek erişimi ver',
    hours: 'Süre (saat)',
    reason: 'Gerekçe',
    reasonHint: 'Destek talebinizin konusu (ör. "Teklif ekranındaki sorun").',
    reasonError: 'En az 3 karakterlik bir gerekçe yazın.',
    submit: 'İzin ver ve kodu göster',
    granted: 'İzin verildi',
    grantFailed: 'İzin verilemedi',
    tooMany: 'Aynı anda en çok 3 açık izin olabilir; önce birini iptal edin.',
    codeTitle: 'Tek kullanımlık kod',
    codeNote:
      'Bu kod yalnız şimdi gösterilir ve bir kez kullanılabilir. Kodu yalnız destek görevlisiyle, güvenli bir yoldan paylaşın.',
    codeLabel: 'Kod',
    listTitle: 'İzinler',
    empty: 'Henüz destek erişim izni verilmedi.',
    columns: {
      reason: 'Gerekçe',
      status: 'Durum',
      expires: 'Bitiş',
      session: 'Oturum',
      by: 'Veren',
    },
    noSession: 'Kod kullanılmadı',
    session: (operator: string, n: number) => `${operator} · ${formatCount(n)} istek`,
    revoke: 'İptal et',
    revokeTitle: 'İzni iptal et',
    revokeBody: 'Açık destek oturumu hemen sonlanır. Bu işlem geri alınamaz.',
    revoked: 'İzin iptal edildi',
    revokeFailed: 'İzin iptal edilemedi',
    auditLink: 'Destek erişimi kayıtlarını denetim kayıtlarında gör',
  },

  // --- Sayfa: hesap kapatmayı iptal (oturumsuz) ---
  // --- Taşınabilir çalışma alanı paketi (v3.1 A8) ---
  workspaceExport: {
    title: 'Çalışma alanını dışa aktar',
    intro:
      'Çalışma alanının bütün verisini tek bir ZIP paketinde indirin: modül başına makine okunur dosyalar (JSON satırları), dosya ekleri ve dosya listesi, SHA-256 özetli manifest. Paket yönetici kapsamındadır; kayıt görünürlüğünüzle sınırlanmaz. Bu paket dışa aktarma içindir: paketi başka bir çalışma alanına yükleyen bir içe aktarma özelliği şu an yoktur.',
    includedTitle: 'Pakete girenler',
    excludedTitle: 'Pakete girmeyenler',
    start: 'Dışa aktarmayı başlat',
    dialogTitle: 'Çalışma alanını dışa aktar',
    dialogIntro: (hours: number) =>
      `Paket arka planda hazırlanır; büyük çalışma alanlarında birkaç dakika sürebilir. Hazır olunca bu ekrandan ${String(hours)} saat boyunca indirilebilir, sonra kendiliğinden silinir.`,
    submit: 'Başlat',
    started: 'Dışa aktarma başladı',
    history: 'Dışa aktarma geçmişi',
    empty: 'Henüz dışa aktarma yok.',
    columns: {
      created: 'Başlatma',
      status: 'Durum',
      progress: 'İlerleme',
      expires: 'Son indirme',
      by: 'İsteyen',
      action: 'İşlem',
    },
    progress: (files: number, rows: number) =>
      `${formatCount(files)} dosya · ${formatCount(rows)} kayıt satırı`,
    totals: (files: number, size: string, skipped: number) =>
      `${formatCount(files)} dosya, ${size}${skipped > 0 ? ` · ${formatCount(skipped)} ek pakete alınmadı` : ''}`,
    download: 'İndir',
    downloadFailed: 'İndirme başlatılamadı',
    failedReason: 'Neden',
    viaClosure: 'kapatma sürecinde',
    closureHint:
      'Hesap kapatma başlatıldıktan sonra (dondurma süresinde) giriş kapalıdır; paketi giriş sayfasındaki "Hesap kapatmayı iptal et" bağlantısından açılan "Kapatma sürecinde veriyi dışa aktar" sayfasında, sahip bilgileriyle alabilirsiniz.',
    ownerOnlyHint: 'Dışa aktarma yönetici yetkisi ve parolanızla yeniden doğrulama ister.',
  },
  closureExport: {
    title: 'Kapatma sürecinde veriyi dışa aktar',
    intro:
      'Çalışma alanınızın kapatılması başlatıldıysa dondurma süresi boyunca bütün veriyi tek pakette indirebilirsiniz. Çalışma alanı sahibi olarak doğrulayın.',
    submit: 'Paketi hazırla',
    preparing: 'Paket hazırlanıyor. Bu sayfa açık kaldıkça durum kendiliğinden yenilenir.',
    ready: 'Paket hazır.',
    download: 'Paketi indir',
    failed:
      'Doğrulanamadı. Bilgileri denetleyin; çalışma alanı kapatma sürecinde olmayabilir ya da dondurma süresi dolmuş olabilir.',
    expired: 'Erişim süresi doldu. Bilgilerinizi yeniden girin.',
    buildFailed: 'Paket hazırlanamadı. Bir süre sonra yeniden deneyin.',
    toCancel: 'Kapatmayı iptal et',
  },
  cancelClosure: {
    title: 'Hesap kapatmayı iptal et',
    intro:
      'Çalışma alanınızın kapatılması başlatıldıysa, dondurma süresi dolmadan çalışma alanı sahibi olarak iptal edebilirsiniz.',
    slug: 'Çalışma alanı kısa adı',
    email: 'Sahip e-posta adresi',
    submit: 'Kapatmayı iptal et',
    done: 'Kapatma iptal edildi. Çalışma alanınıza yeniden giriş yapabilirsiniz.',
    toLogin: 'Giriş sayfasına git',
    toExport: 'Kapatmadan önce verinizi dışa aktarın',
    failed:
      'İptal edilemedi. Bilgileri denetleyin; çalışma alanı kapatılmıyor ya da dondurma süresi dolmuş olabilir.',
  },
} as const;
